import axios from 'axios';
import { API_ENDPOINTS } from './urls';
import { store } from '../../redux/store';
import { setUserPermissions } from '../../redux/slices/access/permissionSlice';

// --- Global Concurrency Variables ---
let isRefreshing = false;
let failedQueue: any = [];

const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}`,
  timeout: 30000,
  withCredentials: true,
});

// REQUEST INTERCEPTOR: Add access token to requests
axiosInstance.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Function to process the queue of failed requests
const processQueue = (error, accessToken = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      // Resolve the original request to allow it to be retried
      // When using cookies, we don't need to pass the accessToken back,
      // as the browser will automatically include the refreshed cookie on retry.
      prom.resolve(accessToken);
    }
  });
  failedQueue = [];
};

//----------------------------------------
// B. RESPONSE INTERCEPTOR: Handle 401 Unauthorized
// ----------------------------------------
axiosInstance.interceptors.response.use(
  (response) => response.data, // Directly return successful responses

  async (error) => {
    const originalRequest = error.config;

    // Check for 401 and ensure we haven't already marked it for retry
    if (error.response?.status === 401 && !originalRequest._retry) {
      // 1. Queue Handling (Race Condition Prevention)
      if (isRefreshing) {
        // If a refresh is already happening, queue this request and wait
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then(() => axiosInstance(originalRequest)) // Retry after refresh completes
          .catch((err) => Promise.reject(err));
      }

      // 2. Initial Refresh Trigger
      originalRequest._retry = true; // Mark request as retried
      isRefreshing = true;

      try {
        // CALL TO REFRESH TOKEN ENDPOINT
        // Browser automatically sends the Refresh Token via the HttpOnly cookie.
        const response = await axiosInstance.post(
          `${API_ENDPOINTS.TOKEN_REFRESH}`
        );

        // set privledge in redux

        // Store new access token
        if (response.data.accessToken) {
          localStorage.setItem('accessToken', response.data.accessToken);
        }
        if (response.data.permissions) {
          localStorage.setItem(
            'permissions',
            JSON.stringify(response.data.permissions)
          );
        }
        store.dispatch(
          setUserPermissions({
            privileges: response.data.permissions,
            isAuthenticated: true,
            isRehydrating: false,
          })
        );
        // 4. Resolve Queue and Retry Original Request
        isRefreshing = false;
        processQueue(null, response.data.accessToken);

        // Return the request with the new token
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // 5. Refresh Failed (Refresh Token expired/invalid)
        isRefreshing = false;
        processQueue(refreshError, null);

        // Clear Redux authentication state
        store.dispatch(
          setUserPermissions({
            privileges: [],
            isAuthenticated: false,
            isRehydrating: false,
          })
        );

        // Clear stored tokens
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
        localStorage.removeItem('permissions');

        // Perform app-level logout (e.g., redirect to login page)
        window.location.href = '/login';

        return Promise.reject(refreshError);
      }
    }
    if (error.code === 'ECONNABORTED') {
      // Timeout error
      return Promise.reject({
        status: 408,
        message: 'Request timed out. Please try again later.',
      });
    } else if (error.response) {
      // Server responded with an error status code

      return Promise.reject({
        status: error.response.status,
        data: error.response.data,
        message:
          error.response.data?.message ||
          'An error occurred. Please try again.',
      });
    }
    // Network or other errors
    return Promise.reject({
      status: 500,
      message: 'A network error occurred. Please check your connection.',
    });
  }
);

export default axiosInstance;
